import { useState, useEffect, useCallback } from 'react';
import { Loader2, Plus, Rocket, ChevronRight, CheckCircle2 } from 'lucide-react';
import { workshopApi } from '../../../services/api';
import { type Feature, type FeatureStatus, FEATURE_STATUS_LABEL } from '../../../types';

const STATUS_BG: Record<FeatureStatus, string> = {
  planned:     'bg-gray-100 dark:bg-surface-850 text-gray-700 dark:text-gray-300',
  in_progress: 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300',
  in_uat:      'bg-teal-100 dark:bg-teal-900/30 text-teal-700 dark:text-teal-300',
  shipped:     'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300',
  cancelled:   'bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400',
};

function slugify(s: string): string {
  return s.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '').slice(0, 90) || 'feature';
}

export default function FeaturesView({ onOpenFeature }: { onOpenFeature: (id: string) => void }) {
  const [features, setFeatures] = useState<Feature[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showNewForm, setShowNewForm] = useState(false);
  const [draftName, setDraftName] = useState('');
  const [draftSlug, setDraftSlug] = useState('');
  const [draftProblem, setDraftProblem] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const fetchFeatures = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setFeatures(await workshopApi.listFeatures());
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load features');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchFeatures(); }, [fetchFeatures]);

  const handleCreate = async () => {
    if (!draftName.trim()) return;
    setSubmitting(true);
    try {
      const created = await workshopApi.createFeature({
        name: draftName.trim(),
        slug: draftSlug || slugify(draftName),
        problem_statement: draftProblem.trim() || undefined,
      });
      setFeatures((prev) => [created, ...prev]);
      setDraftName(''); setDraftSlug(''); setDraftProblem('');
      setShowNewForm(false);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to create feature');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="flex flex-col h-full">
      <div className="flex items-center justify-between mb-3">
        <p className="text-xs text-gray-500 dark:text-gray-400">
          Features bundle related backlog items together with the problem statement, approach, implementation notes, and UAT checklist that explain the work.
        </p>
        <button
          onClick={() => setShowNewForm(true)}
          className="inline-flex items-center gap-1 px-3 py-1.5 text-sm bg-primary-600 hover:bg-primary-700 text-white rounded font-medium"
        >
          <Plus size={14} />
          New feature
        </button>
      </div>

      {showNewForm && (
        <div className="mb-3 p-3 border border-gray-200 dark:border-surface-800 rounded bg-white dark:bg-surface-900 space-y-2">
          <input
            type="text"
            value={draftName}
            onChange={(e) => { setDraftName(e.target.value); if (!draftSlug) setDraftSlug(slugify(e.target.value)); }}
            placeholder="Feature name (e.g. 'Contrast inference engine')"
            className="w-full px-2 py-1.5 text-sm border border-gray-300 dark:border-surface-800 rounded bg-white dark:bg-surface-850 text-gray-900 dark:text-white"
          />
          <input
            type="text"
            value={draftSlug}
            onChange={(e) => setDraftSlug(slugify(e.target.value))}
            placeholder="Slug (auto-derived)"
            className="w-full px-2 py-1.5 text-xs font-mono border border-gray-300 dark:border-surface-800 rounded bg-white dark:bg-surface-850 text-gray-900 dark:text-white"
          />
          <textarea
            value={draftProblem}
            onChange={(e) => setDraftProblem(e.target.value)}
            placeholder="Problem statement (markdown, optional)"
            rows={3}
            className="w-full px-2 py-1.5 text-sm border border-gray-300 dark:border-surface-800 rounded bg-white dark:bg-surface-850 text-gray-900 dark:text-white"
          />
          <div className="flex items-center gap-2 justify-end">
            <button
              onClick={() => { setShowNewForm(false); setDraftName(''); setDraftSlug(''); setDraftProblem(''); }}
              className="px-2 py-1 text-xs text-gray-500 hover:bg-gray-100 dark:hover:bg-surface-850 rounded"
            >
              Cancel
            </button>
            <button
              onClick={handleCreate}
              disabled={submitting || !draftName.trim()}
              className="px-2 py-1 text-xs bg-primary-600 hover:bg-primary-700 disabled:opacity-50 text-white rounded font-medium"
            >
              {submitting ? 'Creating…' : 'Create'}
            </button>
          </div>
        </div>
      )}

      {error && (
        <div className="p-2 mb-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800/40 rounded text-xs text-red-700 dark:text-red-300">{error}</div>
      )}

      <div className="flex-1 min-h-0 overflow-y-auto">
        {loading ? (
          <div className="p-4 text-center text-sm text-gray-500"><Loader2 size={14} className="animate-spin inline mr-1" />Loading…</div>
        ) : features.length === 0 ? (
          <div className="p-4 text-center text-sm text-gray-500">No features yet. Create one and start grouping backlog items.</div>
        ) : (
          <div className="grid gap-2">
            {features.map((f) => {
              const uatTotal = f.uat_total_count ?? 0;
              const uatPass = f.uat_pass_count ?? 0;
              const uatPct = uatTotal > 0 ? Math.round((uatPass / uatTotal) * 100) : null;
              return (
                <button
                  key={f.id}
                  onClick={() => onOpenFeature(f.id)}
                  className="text-left p-3 border border-gray-200 dark:border-surface-800 rounded bg-white dark:bg-surface-900 hover:border-primary-400 dark:hover:border-primary-700 transition-colors"
                >
                  <div className="flex items-center gap-2">
                    <Rocket size={14} className="text-primary-600 dark:text-primary-400" />
                    <span className="text-sm font-semibold text-gray-900 dark:text-white">{f.name}</span>
                    <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${STATUS_BG[f.status]}`}>
                      {FEATURE_STATUS_LABEL[f.status]}
                    </span>
                    <span className="text-[10px] text-gray-400 font-mono ml-1">{f.slug}</span>
                    <ChevronRight size={14} className="ml-auto text-gray-400" />
                  </div>
                  {f.problem_statement && (
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 line-clamp-2">{f.problem_statement}</p>
                  )}
                  <div className="flex items-center gap-3 mt-2 text-[11px] text-gray-500 dark:text-gray-400">
                    <span>{f.item_count ?? 0} items</span>
                    {uatTotal > 0 && (
                      <span className="inline-flex items-center gap-0.5">
                        <CheckCircle2 size={10} className={uatPct === 100 ? 'text-green-600' : 'text-gray-400'} />
                        {uatPass}/{uatTotal} UAT {uatPct !== null && `(${uatPct}%)`}
                      </span>
                    )}
                    {f.shipped_at && <span className="text-green-600">shipped {new Date(f.shipped_at).toLocaleDateString()}</span>}
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
